interface AdminLineChartProps {
	data: number[];
	color?: string;
	height?: number;
	labels?: string[];
	yLabels?: string[];
}

export function AdminLineChart({
	data,
	color = "var(--a-terracotta)",
	height = 200,
	labels = [],
	yLabels = [],
}: AdminLineChartProps) {
	const w = 800;
	const h = height;
	const padL = 36;
	const padR = 12;
	const padT = 12;
	const padB = 28;
	const innerW = w - padL - padR;
	const innerH = h - padT - padB;
	const min = 0;
	// Une série entièrement à zéro — le cas d'une boutique qui n'a pas encore
	// vendu — donnait `max = 0`, donc une division 0/0 : toutes les coordonnées
	// partaient en NaN et le navigateur rejetait le tracé (« attribute d:
	// Expected number »). On garde une échelle minimale pour poser la ligne à
	// plat sur l'axe plutôt que de ne rien dessiner.
	const sommet = Math.max(0, ...data) * 1.1;
	const max = sommet > 0 ? sommet : 1;
	// Un point unique ne définit pas d'intervalle : il se place à gauche.
	const pas = data.length > 1 ? innerW / (data.length - 1) : 0;
	const pts = data.map((v, i): [number, number] => {
		const x = padL + i * pas;
		const y = padT + innerH - ((v - min) / (max - min)) * innerH;
		return [x, y];
	});
	const linePath = pts
		.map(
			(p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`,
		)
		.join(" ");
	const areaPath = pts.length
		? `${linePath} L ${pts[pts.length - 1][0]} ${padT + innerH} L ${pts[0][0]} ${padT + innerH} Z`
		: "";
	const gridY = 4;

	return (
		<svg
			viewBox={`0 0 ${w} ${h}`}
			style={{ width: "100%", height: h, display: "block" }}
			role="img"
			aria-label="Graphique linéaire"
		>
			<title>Graphique linéaire</title>
			<defs>
				<linearGradient id="aChartGrad" x1="0" x2="0" y1="0" y2="1">
					<stop offset="0" stopColor={color} stopOpacity="0.18" />
					<stop offset="1" stopColor={color} stopOpacity="0" />
				</linearGradient>
			</defs>
			{Array.from({ length: gridY + 1 }, (_, i) => {
				const y = padT + (i / gridY) * innerH;
				return (
					<line
						key={`grid-line-${y}`}
						x1={padL}
						y1={y}
						x2={w - padR}
						y2={y}
						stroke="var(--a-border)"
						strokeWidth="1"
					/>
				);
			})}
			{yLabels.map((l, i) => {
				// Une seule graduation ne définit pas d'intervalle : elle se pose au bas
				// de l'axe plutôt que de produire une coordonnée invalide.
				const y =
					padT +
					innerH -
					(yLabels.length > 1 ? i / (yLabels.length - 1) : 0) * innerH +
					3;
				return (
					<text
						key={`y-label-${l}-${y}`}
						x={padL - 8}
						y={y}
						textAnchor="end"
						fontSize="10"
						fill="var(--a-ink-mute)"
						fontFamily="var(--a-font-mono)"
					>
						{l}
					</text>
				);
			})}
			{labels.map((l, i) => {
				const x =
					padL + (labels.length > 1 ? i / (labels.length - 1) : 0) * innerW;
				return (
					<text
						key={`x-label-${l}-${x}`}
						x={x}
						y={h - 10}
						textAnchor="middle"
						fontSize="10"
						fill="var(--a-ink-mute)"
					>
						{l}
					</text>
				);
			})}
			<path d={areaPath} fill="url(#aChartGrad)" />
			<path
				d={linePath}
				fill="none"
				stroke={color}
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			{pts.map((p, i) =>
				i === pts.length - 1 ? (
					<g key={`pt-last-${p[0]}-${p[1]}`}>
						<circle cx={p[0]} cy={p[1]} r="6" fill={color} opacity="0.18" />
						<circle
							cx={p[0]}
							cy={p[1]}
							r="3"
							fill={color}
							stroke="var(--a-surface)"
							strokeWidth="1.5"
						/>
					</g>
				) : null,
			)}
		</svg>
	);
}
